import React, { useEffect, useRef, useState } from 'react'
import type { CSSProperties } from 'react'
import styles from './VerticalEntryForm.module.css'
import { useStepContext } from '@/components/Context/StepContext'
import { generateUrlSafeBase64 } from '@/components/utils/securityUtils'
import { handleFormSubmission } from '@/components/utils/formSubmissionUtils'
import { SERVICE_TYPES } from '@/components/constants'
import { WorkingStatusVertical } from '../Steps/StepContent/WorkingStatusVertical'
import { ExperienceYearsVertical } from '../Steps/StepContent/ExperienceYearsVertical'
import { PrefectureVertical } from '../Steps/StepContent/PrefectureVertical'
import { FullNameVertical } from '../Steps/StepContent/FullNameVertical'
import { TellVertical } from '../Steps/StepContent/TellVertical'
import StepCounter from '../Steps/StepCounter'
import { EntryFormProps } from './types'
import { JobChangeInterestModal } from '../FormGuidance/JobChangeInterestModal'

const TOTAL_STEPS = 5

// 縦型EF（DEV-3061のA/Bテスト結果を受けDEV-3300で正式採用）。各stepの縦型専用コンポを
// currentStep 駆動で縦積み表示する。
const VerticalEntryForm = ({ prefectures, config }: EntryFormProps) => {
    const { currentStep, formData } = useStepContext()

    const [registerToken, setRegisterToken] = useState('')
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [isStuck, setIsStuck] = useState(false)
    // DEV-3061: 転職意欲確認ポップ(初手ポップ)。横型EntryFormと同じくstep0扱いで表示する
    const [step0ModalCompleted, setStep0ModalCompleted] = useState(false)

    useEffect(() => {
        setRegisterToken(generateUrlSafeBase64())
    }, [])

    const cards = [
        <WorkingStatusVertical gtmTags={config.stepFormLabels.step1} stepNumber={1} />,
        <ExperienceYearsVertical gtmTags={config.stepFormLabels.step2} stepNumber={2} />,
        <PrefectureVertical prefectures={prefectures} gtmTags={config.stepFormLabels.step3} stepNumber={3} />,
        <FullNameVertical gtmTags={config.stepFormLabels.step4} stepNumber={4} />,
        <TellVertical gtmTags={config.stepFormLabels.step5} stepNumber={5} isSubmitting={isSubmitting} />,
    ]

    // 新しいカードが出現（currentStepが前進）したらそのカードへ自動スクロール
    const cardRefs = useRef<Array<HTMLDivElement | null>>([])
    const prevStepRef = useRef(currentStep)
    useEffect(() => {
        if (currentStep > prevStepRef.current) {
            cardRefs.current[currentStep]?.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
            })
        }
        prevStepRef.current = currentStep
    }, [currentStep])

    // 通常配置のバナー＋ステッパーが画面上部から見えなくなったら固定版を表示する
    const sentinelRef = useRef<HTMLDivElement | null>(null)
    useEffect(() => {
        const el = sentinelRef.current
        if (!el || typeof IntersectionObserver === 'undefined') return
        const observer = new IntersectionObserver(
            ([entry]) => setIsStuck(!entry.isIntersecting),
            { threshold: 0 }
        )
        observer.observe(el)
        return () => observer.disconnect()
    }, [])

    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        handleFormSubmission(
            e,
            formData,
            registerToken,
            setIsSubmitting,
            SERVICE_TYPES.FOODSLABO
        )
    }

    const banner = (
        <header className={styles.banner}>
            <h2 className={styles.bannerBadge}>簡単30秒!</h2>
            {/* TODO(DEV-3300): ページ内でh1が2つになっているため、SEO観点でPOと相談して解消する */}
            <h1 className={styles.bannerTitle}>無料で求人を紹介してもらう！</h1>
        </header>
    )

    return (
        <section id='entry-form' className={styles.vertical}>
            {config?.stepFormLabels.step0 && !step0ModalCompleted && (
                <JobChangeInterestModal
                    gtmTags={config.stepFormLabels.step0}
                    onComplete={() => {
                        setStep0ModalCompleted(true)
                        window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' })
                    }}
                />
            )}

            {/* 上部画像の下に通常配置（白背景・シャドウなし） */}
            <div className={styles.inFlowHead} ref={sentinelRef}>
                {banner}
                <div className={styles.stepperPlain}>
                    <StepCounter currentIndex={currentStep} totalSteps={TOTAL_STEPS} />
                </div>
            </div>

            {/* 上記が見えなくなったら白背景＋シャドウ版を上部固定 */}
            {isStuck && (
                <div className={styles.fixedHead}>
                    {banner}
                    <div className={styles.stepperFixed}>
                        <StepCounter currentIndex={currentStep} totalSteps={TOTAL_STEPS} />
                    </div>
                </div>
            )}

            <form onSubmit={handleSubmit} className={styles.form}>
                {cards.map((card, i) =>
                    i <= currentStep ? (
                        <div
                            key={i}
                            ref={(el) => {
                                cardRefs.current[i] = el
                            }}
                            className={styles.card}
                            style={
                                {
                                    '--ef-follow-display':
                                        i === currentStep ? 'block' : 'none',
                                } as CSSProperties
                            }
                        >
                            {card}
                        </div>
                    ) : null
                )}
            </form>

            {isSubmitting && <div className={styles.blockingOverlay} />}
        </section>
    )
}

export default VerticalEntryForm
